import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import UISimpleTable from '@/components/shared/UISimpleTable';
import UIButton from '@/components/shared/UIButton';
import UITenantInfo from '@/components/shared/UITenantInfo';
import useGetDebtors from '@/hooks/useGetDebtors';
import useGetPaymentsStatistics from '@/hooks/useGetPaymentsStatistics';
import Skeleton from '@/components/shared/Skeleton';
import './PaymentsDashboardSection.scss';
import UIDashboardPaymentsChart from '@/components/shared/UIDashboardPaymentsChart';
import UIProperty from '@/components/shared/UIProperty';

import { ReactComponent as IconPaid } from '@/img/icon32-paid.svg';
import { ReactComponent as IconDebt } from '@/img/icon32-debt.svg';
import { ReactComponent as IconAlertOctagon } from '@/img/icon32-alert-octagon.svg';
import { ReactComponent as IconBarChart } from '@/img/icon32-bar-chart.svg';

const baseClass = 'DashboardPage__PaymentsDashboardSection';

function PaymentsDashboardSection() {
  const navigate = useNavigate();

  const { data: statsResponse, isPending: isStatsLoading } = useGetPaymentsStatistics();
  const { data: debtorsData, isPending: isDebtorsLoading } = useGetDebtors();

  const chartData = useMemo(() => {
    if (!statsResponse?.data) {
      return {
        total: 0,
        paid: 0,
        pending: 0,
        arrears: 0,
        paidAmount: 0,
        overdueAmount: 0,
      };
    }
    const { total, paid, upcoming, arrears, amounts } = statsResponse.data;
    return {
      total,
      paid,
      pending: upcoming, // Мапимо upcoming на pending для діаграми
      arrears,
      paidAmount: amounts.totalPaid,
      overdueAmount: amounts.totalOverdue,
    };
  }, [statsResponse]);

  const tableData = useMemo(() => {
    if (!debtorsData?.data) {
      return [];
    }
    return debtorsData.data.map((debtor) => ({
      ...debtor.tenant,
      unit: debtor.unit,
      property: debtor.property,
    }));
  }, [debtorsData]);

  const tableColumns = [
    {
      key: 'tenant',
      label: 'Debtor',
      width: '234px',
      sortable: true,
      sortDirection: null as 'asc' | 'desc' | null,
      showDivider: true,
      render: (value: any, row: any) => (
        <div className={`${baseClass}__table-tenant`}>
          <UITenantInfo
            name={row.name}
            email={row.email}
            tenantId={row.id}
            avatar={row.avatar}
            size="medium"
          />
        </div>
      ),
    },
    {
      key: 'unit',
      label: 'Unit',
      width: '91px',
      sortable: true,
      sortDirection: null as 'asc' | 'desc' | null,
      showDivider: true,
      render: (value: any, row: any) => (
        <div className={`${baseClass}__table-unit`}>{row.unit?.number || '-'}</div>
      ),
    },
    {
      key: 'property',
      label: 'Property',
      width: '135px',
      sortable: true,
      sortDirection: null as 'asc' | 'desc' | null,
      showDivider: false,
      render: (value: any, row: any) => (
        <UIProperty name={row.property?.name} type="residential" />
      ),
    },
  ];

  const handleSort = (columnKey: string, direction: 'asc' | 'desc') => {
    console.log('Sort:', columnKey, direction);
  };

  const handleTenantClick = (tenantRow: any) => {
    navigate(`/dashboard/tenants/${tenantRow.id}`);
  };

  return (
    <div className={baseClass}>
      <div className={`${baseClass}__top-section`}>
        <div className={`${baseClass}__header`}>
          <div className={`${baseClass}__title-line`}>
            <div className={`${baseClass}__title-text`}>
              <span>Rent payments:</span>
              <span className={`${baseClass}__title-period`}>July 2024</span>
            </div>
            <div className={`${baseClass}__menu-icon`}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="1" fill="#64748B" />
                <circle cx="12" cy="5" r="1" fill="#64748B" />
                <circle cx="12" cy="19" r="1" fill="#64748B" />
              </svg>
            </div>
          </div>
          <div className={`${baseClass}__divider`}></div>
        </div>

        <div className={`${baseClass}__statistics-section`}>
          {isStatsLoading ? (
            <Skeleton isLoading={true} style={{ width: '100%', height: '150px' }} />
          ) : (
            <UIDashboardPaymentsChart
              totalValue={chartData.total}
              centerIcon={<IconBarChart width={64} height={64} />}
              items={[
                {
                  value: chartData.paid,
                  label: 'Paid',
                  color: '#76C102',
                  icon: <IconPaid width={24} height={24} />,
                  amount: chartData.paidAmount, // Передаємо суму
                },
                {
                  value: chartData.pending,
                  label: 'Pending',
                  color: '#FF9C07',
                  icon: <IconAlertOctagon width={24} height={24} />,
                  // Сума для pending не передається
                },
                {
                  value: chartData.arrears,
                  label: 'Arrears',
                  color: '#FB2F54',
                  icon: <IconDebt width={24} height={24} />,
                  amount: chartData.overdueAmount, // Передаємо суму
                },
              ]}
            />
          )}
        </div>
      </div>

      <div className={`${baseClass}__list-section`}>
        <div className={`${baseClass}__list-header`}>
          <div className={`${baseClass}__list-title`}>List of debtor tenants</div>
          <UIButton
            variant="primary"
            size="small"
            onClick={() => navigate('/dashboard/tenants#debtors-section')}
          >
            View All
          </UIButton>
        </div>
        <div className={`${baseClass}__list-divider`}></div>

        {isDebtorsLoading ? (
          <Skeleton isLoading={true} style={{ width: '100%', height: '200px' }} />
        ) : (
          <UISimpleTable
            data={tableData}
            columns={tableColumns}
            className={`${baseClass}__table`}
            onSort={handleSort}
            onRowClick={handleTenantClick}
          />
        )}
      </div>
    </div>
  );
}

export default PaymentsDashboardSection;